// components/TopNav.jsx
import React, { useEffect, useRef, useState, forwardRef } from 'react';
import styled from 'styled-components';
import { createPortal } from 'react-dom';
import codiiiLogo from '../images/CODiii Logo-Horizontal-BlackIcon-Blacktext.png';
import { FiSettings, FiMenu, FiX } from 'react-icons/fi';
import { useFlexibleTranslation } from '../contexts/LanguageContext';
// `planHasCompliance` is deliberately NOT imported while the plan-tier gate is
// commented out below -- an import used only inside a comment is a lint
// warning. Re-add it to this line when restoring that gate at launch.
import { shouldShowReview, shouldShowCommunications, hasComplianceAccess } from '../utils/userAccess';
import { phProjectOpened, phTourSettingsOpened } from '../utils/posthogEvents';
// import { hasReviewAccess } from '../utils/userAccess';

const TopNav = forwardRef(function TopNav(
  {
    user = { name: 'User', email: '' },
    projects = [],
    selectedProject,
    onSelectProject,
    activeTab = 'Chat',
    onTabChange,
    onSignOut,
    onCreateProject,
    onOpenAccount,
    chunkingStatus,
    sandboxMode,
    canSeeCompanyResources,
    planKey,
  },
  ref
) {
  const { t } = useFlexibleTranslation();

  // Project dropdown
  const [open, setOpen] = useState(false);
  const [ddPos, setDdPos] = useState({ left: 0, top: 0, width: 0 });
  const hasProjects = Array.isArray(projects) && projects.length > 0;

  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const prevChunkingActiveRef = useRef(false);

  // Mobile menu
  const [mobileOpen, setMobileOpen] = useState(false);
  const mobileMenuRef = useRef(null);
  const hamburgerRef = useRef(null);

  // Status popover
  const [statusOpen, setStatusOpen] = useState(false);
  const [statusPos, setStatusPos] = useState({ left: 0, top: 0 });
  const statusTriggerRef = useRef(null);
  const statusMenuRef = useRef(null);

  const isChunkingActive = !!chunkingStatus?.active;

  const chunkingPercent =
    typeof chunkingStatus?.percent === 'number'
      ? Math.max(0, Math.min(100, chunkingStatus.percent))
      : null;

  const steps =
    Array.isArray(chunkingStatus?.steps) && chunkingStatus.steps.length > 0
      ? chunkingStatus.steps
      : [
          { label: 'Processing', state: 'active', detail: 'Working in the background…' },
          { label: 'Indexing', state: 'todo' },
          { label: 'Ready', state: 'todo' },
        ];

  // close on outside click (project menu, status popover, mobile menu)
  useEffect(() => {
    const onDocClick = (e) => {
      const projTrig = triggerRef.current;
      const projMenu = menuRef.current;

      const statTrig = statusTriggerRef.current;
      const statMenu = statusMenuRef.current;

      const clickedProjectTrigger = projTrig?.contains(e.target);
      const clickedProjectMenu = projMenu?.contains(e.target);

      const clickedStatusTrigger = statTrig?.contains(e.target);
      const clickedStatusMenu = statMenu?.contains(e.target);

      const clickedMobileMenu = mobileMenuRef.current?.contains(e.target);
      const clickedHamburger = hamburgerRef.current?.contains(e.target);

      if (!clickedProjectTrigger && !clickedProjectMenu) setOpen(false);
      if (!clickedStatusTrigger && !clickedStatusMenu) setStatusOpen(false);
      if (!clickedMobileMenu) setMobileOpen(false);
    };

    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

    useEffect(() => {
      const prev = prevChunkingActiveRef.current;
      const now = !!chunkingStatus?.active;

      // open popover when chunking starts
      if (!prev && now) {
        if (statusTriggerRef.current) {
          const rect = statusTriggerRef.current.getBoundingClientRect();
          setStatusPos({
            left: rect.right + window.scrollX,
            top: rect.bottom + 10 + window.scrollY,
          });
        }

        setStatusOpen(true);
      }

      // close automatically when chunking finishes
      if (prev && !now) {
        setStatusOpen(false);
      }

      prevChunkingActiveRef.current = now;
    }, [chunkingStatus?.active]);

  const handleOpen = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();

    setDdPos({
      left: rect.left + window.scrollX,
      top: rect.bottom + 8 + window.scrollY,
      width: rect.width,
    });

    setOpen((v) => !v);
  };

  const handleStatusOpen = () => {
    if (!statusTriggerRef.current) return;
    const rect = statusTriggerRef.current.getBoundingClientRect();

    setStatusPos({
      left: rect.right + window.scrollX,
      top: rect.bottom + 10 + window.scrollY,
    });

    // don’t let both menus be open at once
    setOpen(false);
    setStatusOpen((v) => !v);
  };

  function ProjectMenuPortal({
    open,
    ddPos,
    projects,
    selectedProject,
    onSelectProject,
    onCreateProject,
    menuRef,
    sandboxMode,
  }) {
    if (!open) return null;

    return createPortal(
      <ProjectDropdown
        ref={menuRef}
        className="show"
        style={{ left: ddPos.left, top: ddPos.top, width: ddPos.width }}
        role="menu"
      >
        {projects.map((p) => {
          const active = p.id === selectedProject?.id;
          return (
            <ProjectDropdownItem
              key={p.id}
              className={active ? 'active' : ''}
              onClick={() => {
                phProjectOpened({ projectId: p.id, projectName: p.name });
                onSelectProject?.(p);
              }}
              role="menuitem"
              type="button"
            >
              {p.name}
            </ProjectDropdownItem>
          );
        })}

        <ProjectDropdownDivider role="separator" />

        {!sandboxMode && (
          <ProjectDropdownItem
            className="create"
            onClick={() => onCreateProject?.()}
            disabled={!onCreateProject}
            role="menuitem"
            type="button"
          >
            ➕ {t('project.createProject', 'Create Project')}
          </ProjectDropdownItem>
        )}
      </ProjectDropdown>,
      document.body
    );
  }

  function StatusPopoverPortal({ open, pos, steps, percent, title, menuRef }) {
    if (!open) return null;

    return createPortal(
      <StatusPopover
        ref={menuRef}
        style={{ left: pos.left, top: pos.top }}
        role="dialog"
        aria-label="Processing status"
      >
        <StatusHeader>
          <div className="title">{title || 'Processing'}</div>
          {typeof percent === 'number' ? <div className="pct">{percent}%</div> : null}
        </StatusHeader>

        <StatusDivider />

        <StatusList role="list">
          {steps.map((s, idx) => (
            <StatusRow key={`${s.label}-${idx}`} role="listitem">
              <StatusDot data-state={s.state} aria-hidden="true" />
              <div className="text">
                <div className="label">{s.label}</div>
                {s.detail ? <div className="detail">{s.detail}</div> : null}
              </div>
              <div className="state">
                {s.state === 'done'
                  ? '✓'
                  : s.state === 'error'
                  ? '!'
                  : s.state === 'active'
                  ? '…'
                  : ''}
              </div>
            </StatusRow>
          ))}
        </StatusList>

        <StatusDivider />

        <StatusFooter>
          <span className="hint">This runs in the background.</span>
        </StatusFooter>
      </StatusPopover>,
      document.body
    );
  }

  return (
    <Nav ref={ref}>
      <NavContent>
        {/* LEFT: logo + project selector + nav links */}
        <LeftGroup>
          <LogoWrap
            role="button"
            tabIndex={0}
            aria-label="Go to Research"
            onClick={() => {
              if (activeTab !== 'Chat') {
                onTabChange?.('Chat');
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onTabChange?.('Chat');
              }
            }}
          >
            <img src={codiiiLogo} alt="CODiii Logo" />
          </LogoWrap>

          <ProjectSelectorWrap>
            <ProjectSelectorButton
              ref={triggerRef}
              onClick={
                hasProjects
                  ? handleOpen
                  : () => {
                      setOpen(false);
                      onCreateProject?.();
                    }
              }
              className={open ? 'open' : ''}
              aria-haspopup={hasProjects ? 'menu' : undefined}
              aria-expanded={hasProjects ? open : undefined}
              data-empty={!hasProjects}
              type="button"
            >
              <span className="selected-text">
                {hasProjects
                  ? selectedProject?.name || t('common.selectProject', 'Select Project')
                  : `➕ ${t('project.createProject', 'Create Project')}`}
              </span>
              {hasProjects && <span className="dropdown-arrow">▼</span>}
            </ProjectSelectorButton>

            {hasProjects && (
              <ProjectMenuPortal
                open={open}
                ddPos={ddPos}
                projects={projects}
                selectedProject={selectedProject}
                onSelectProject={(p) => {
                  onSelectProject?.(p);
                  setOpen(false);
                }}
                onCreateProject={() => {
                  setOpen(false);
                  onCreateProject?.();
                }}
                menuRef={menuRef}
                sandboxMode={sandboxMode}
              />
            )}
          </ProjectSelectorWrap>

          <NavLinks>
            <NavLink
              className={activeTab === 'Chat' ? 'active' : ''}
              onClick={() => onTabChange?.('Chat')}
              type="button"
              data-tour-nav="research"
            >
              {t('navigation.research', 'Research')}
            </NavLink>

            {!sandboxMode && (
              <>
                <NavLink
                  className={activeTab === 'Requirements' ? 'active' : ''}
                  onClick={() => onTabChange?.('Requirements')}
                  type="button"
                  data-tour-nav="link-folder"
                >
                  {t('navigation.linkFolders', 'Link Folders')}
                </NavLink>
                <NavLink
                  className={activeTab === 'ExtractedRequirements' ? 'active' : ''}
                  onClick={() => onTabChange?.('ExtractedRequirements')}
                  type="button"
                  data-tour-nav="register"
                >
                  {t('navigation.extractedRequirements', 'Requirements Register')}
                </NavLink>
                {shouldShowReview(user?.email) && (
                  <NavLink
                    className={activeTab === 'Review' ? 'active' : ''}
                    onClick={() => onTabChange?.('Review')}
                    type="button"
                  >
                    {t('navigation.review', 'Review')}
                  </NavLink>
                )}

                {shouldShowCommunications(user?.email) && (
                  <NavLink
                    className={activeTab === 'Communications' ? 'active' : ''}
                    onClick={() => onTabChange?.('Communications')}
                    type="button"
                    data-tour-nav="communications"
                  >
                    {t('navigation.communications', 'Communications')}
                  </NavLink>
                )}

                {/* Plan-tier access is COMMENTED OUT, not deleted -- the rule itself is
                    correct and is what should govern this at launch. Until then the
                    Compliance Tracker has not launched, and the OR meant every studio
                    and firm plan saw it in their nav regardless of the allowlist, which
                    also made `COMPLIANCE_ENABLED_FOR_ALL = false` have no effect for
                    those tiers. Restore the original line at launch:
                      (planHasCompliance(planKey) || hasComplianceAccess(user?.email)) */}
                {hasComplianceAccess(user?.email) && (
                  <NavLink
                    className={activeTab === 'Compliance' ? 'active' : ''}
                    onClick={() => onTabChange?.('Compliance')}
                    type="button"
                    data-tour-nav="compliance-scan"
                  >
                    {t('navigation.complianceTracker', 'Compliance Tracker')}
                  </NavLink>
                )}

                <NavLink
                  className={activeTab === 'Settings' ? 'active' : ''}
                  onClick={() => { phTourSettingsOpened(); onTabChange?.('Settings'); }}
                  type="button"
                  data-tour-nav="settings"
                >
                  {t('navigation.settings', 'Project Settings')}
                </NavLink>
              </>
            )}
          </NavLinks>
        </LeftGroup>

        {/* RIGHT: user + status + gear + sign out */}
        <RightGroup>
          <UserInfo>
            <span className="name">{user?.name || 'User'}</span>

            {isChunkingActive && (
              <StatusIconButton
                ref={statusTriggerRef}
                type="button"
                aria-label="Open processing status"
                title="Processing status"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleStatusOpen();
                }}
              >
                <StatusRing aria-hidden="true">
                  <StatusRingFill
                    $indeterminate={typeof chunkingPercent !== 'number'}
                    style={{
                      ['--p']: typeof chunkingPercent === 'number' ? chunkingPercent : 30,
                    }}
                  />
                </StatusRing>
              </StatusIconButton>
            )}

            <GearIconButton
              aria-label="Open user settings"
              title="Open user settings"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onOpenAccount?.();
              }}
              type="button"
            >
              <FiSettings />
            </GearIconButton>
          </UserInfo>

          <SignOutBtn onClick={onSignOut} type="button">
            {t('account.signOut', 'Sign Out')}
          </SignOutBtn>

          <HamburgerBtn
            ref={hamburgerRef}
            type="button"
            data-tour-nav-hamburger
            aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={mobileOpen}
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
              setStatusOpen(false);
              setMobileOpen((v) => !v);
            }}
          >
            {mobileOpen ? <FiX /> : <FiMenu />}
          </HamburgerBtn>
        </RightGroup>
      </NavContent>

      <MobileMenu ref={mobileMenuRef} $open={mobileOpen} role="navigation" aria-label="Mobile navigation">
        <MobileNavLink
          className={activeTab === 'Chat' ? 'active' : ''}
          onClick={() => { onTabChange?.('Chat'); setMobileOpen(false); }}
          type="button"
          data-tour-nav="research"
        >
          {t('navigation.research', 'Research')}
        </MobileNavLink>

        {!sandboxMode && (
          <>
            <MobileNavLink
              className={activeTab === 'Requirements' ? 'active' : ''}
              onClick={() => { onTabChange?.('Requirements'); setMobileOpen(false); }}
              type="button"
              data-tour-nav="link-folder"
            >
              {t('navigation.linkFolders', 'Link Folders')}
            </MobileNavLink>
            <MobileNavLink
              className={activeTab === 'ExtractedRequirements' ? 'active' : ''}
              onClick={() => { onTabChange?.('ExtractedRequirements'); setMobileOpen(false); }}
              type="button"
              data-tour-nav="register"
            >
              {t('navigation.extractedRequirements', 'Requirements Register')}
            </MobileNavLink>
            {shouldShowReview(user?.email) && (
              <MobileNavLink
                className={activeTab === 'Review' ? 'active' : ''}
                onClick={() => { onTabChange?.('Review'); setMobileOpen(false); }}
                type="button"
              >
                {t('navigation.review', 'Review')}
              </MobileNavLink>
            )}
            {shouldShowCommunications(user?.email) && (
              <MobileNavLink
                className={activeTab === 'Communications' ? 'active' : ''}
                onClick={() => { onTabChange?.('Communications'); setMobileOpen(false); }}
                type="button"
                data-tour-nav="communications"
              >
                {t('navigation.communications', 'Communications')}
              </MobileNavLink>
            )}
            {/* Plan-tier access commented out here too -- see the desktop nav above.
                  Both must change together or the tracker is hidden on one and not
                  the other. Restore at launch:
                    (planHasCompliance(planKey) || hasComplianceAccess(user?.email)) */}
            {hasComplianceAccess(user?.email) && (
              <MobileNavLink
                className={activeTab === 'Compliance' ? 'active' : ''}
                onClick={() => { onTabChange?.('Compliance'); setMobileOpen(false); }}
                type="button"
                data-tour-nav="compliance-scan"
              >
                {t('navigation.complianceTracker', 'Compliance Tracker')}
              </MobileNavLink>
            )}
            <MobileNavLink
              className={activeTab === 'Settings' ? 'active' : ''}
              onClick={() => { phTourSettingsOpened(); onTabChange?.('Settings'); setMobileOpen(false); }}
              type="button"
              data-tour-nav="settings"
            >
              {t('navigation.settings', 'Project Settings')}
            </MobileNavLink>
          </>
        )}

        <MobileMenuFooter>
          <MobileMenuDivider />
          <MobileUserLabel>{user?.name || 'User'}</MobileUserLabel>
          <MobileFooterActions>
            {isChunkingActive && (
              <StatusIconButton
                type="button"
                aria-label="Open processing status"
                title="Processing status"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleStatusOpen();
                  setMobileOpen(false);
                }}
              >
                <StatusRing aria-hidden="true">
                  <StatusRingFill
                    $indeterminate={typeof chunkingPercent !== 'number'}
                    style={{
                      ['--p']: typeof chunkingPercent === 'number' ? chunkingPercent : 30,
                    }}
                  />
                </StatusRing>
              </StatusIconButton>
            )}
            <MobileFooterBtn
              type="button"
              aria-label="Open user settings"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onOpenAccount?.();
                setMobileOpen(false);
              }}
            >
              <FiSettings />
              Account
            </MobileFooterBtn>
            <MobileFooterBtn
              type="button"
              className="sign-out"
              onClick={() => {
                onSignOut?.();
                setMobileOpen(false);
              }}
            >
              {t('account.signOut', 'Sign Out')}
            </MobileFooterBtn>
          </MobileFooterActions>
        </MobileMenuFooter>
      </MobileMenu>

      {isChunkingActive && (
        <StatusPopoverPortal
          open={statusOpen}
          pos={statusPos}
          steps={steps}
          percent={chunkingPercent}
          title={chunkingStatus?.title}
          menuRef={statusMenuRef}
        />
      )}
    </Nav>
  );
});

export default TopNav;

/* =======================
   Styled Components
======================= */

const Nav = styled.nav`
  background: rgba(233, 112, 36, 0.9);
  backdrop-filter: blur(10px);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;

  padding: 12px 32px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow-x: clip;

  @media (max-width: 768px) {
    padding: 12px 16px;
  }

  @media (max-width: 520px) {
    padding: 10px 12px;
  }
`;

const NavContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  min-width: 0;

  @media (max-width: 520px) {
    gap: 10px;
  }
`;

const LeftGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  min-width: 0;
  flex: 1 1 auto;
  overflow: hidden;

  @media (max-width: 900px) {
    gap: 12px;
  }

  @media (max-width: 520px) {
    gap: 8px;
  }
`;

const LogoWrap = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  flex-shrink: 0;

  img {
    height: 50px;
    width: auto;
    display: block;
    transition: transform 0.15s ease, opacity 0.15s ease;
  }

  &:hover img {
    transform: translateY(-1px);
    opacity: 0.95;
  }

  @media (max-width: 520px) {
    img {
      height: 36px;
    }
  }
`;

const ProjectSelectorWrap = styled.div`
  position: relative;
  min-width: 250px;
  flex: 0 1 280px;

  @media (max-width: 768px) {
    min-width: 120px;
    flex: 1 1 160px;
  }

  @media (max-width: 520px) {
    min-width: 0;
    flex: 1 1 auto;
    max-width: none;
  }
`;

const ProjectSelectorButton = styled.button`
  width: 100%;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.3);

  padding: 10px 44px 10px 18px;
  border-radius: 8px;

  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: space-between;
  user-select: none;

  transition: all 0.2s ease;
  position: relative;
  min-width: 0;

  .selected-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
    min-width: 0;
  }

  .dropdown-arrow {
    position: absolute;
    right: 16px;
    pointer-events: none;
    transition: transform 0.2s ease;
    color: #fff;
  }

  &.open .dropdown-arrow {
    transform: rotate(180deg);
  }

  &:hover {
    background: rgba(0, 0, 0, 0.85);
    border-color: rgba(0, 0, 0, 0.9);
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.85);
    outline-offset: 2px;
  }
`;

const ProjectDropdown = styled.div`
  position: fixed;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;

  opacity: 0;
  visibility: hidden;
  transform: translateY(-10px);
  transition: all 0.2s ease;
  z-index: 2000;

  max-height: min(60vh, 420px);
  overflow-y: auto;

  &.show {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }
`;

const ProjectDropdownItem = styled.button`
  width: 100%;
  padding: 12px 18px;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;

  color: #333;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.15s ease;

  &:hover {
    background: #000;
    color: #fff;
  }

  &.active {
    background: rgba(233, 112, 36, 0.1);
    font-weight: 600;
  }

  &.active:hover {
    background: #000;
    color: #fff;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ProjectDropdownDivider = styled.div`
  height: 1px;
  background: rgba(0, 0, 0, 0.08);
  margin: 4px 0;
`;

const NavLinks = styled.div`
  display: flex;
  gap: 32px;
  align-items: center;
  min-width: 0;

  @media (max-width: 768px) {
    gap: 16px;
  }

  @media (max-width: 1499px) {
    display: none;
  }
`;

const NavLink = styled.button`
  border: 0;
  background: transparent;
  cursor: pointer;

  color: #fff;
  text-decoration: none;
  font-size: 15px;
  font-weight: 500;

  padding: 8px 0;
  position: relative;
  transition: color 0.2s ease;
  white-space: nowrap;

  &:hover {
    color: #000;
  }

  &.active {
    font-weight: 600;
  }

  &.active::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: #fff;
  }
`;

const RightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  flex: 0 0 auto;
  flex-shrink: 0;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  flex-shrink: 0;

  .name {
    white-space: nowrap;

    @media (max-width: 1499px) {
      max-width: 140px;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  @media (max-width: 520px) {
    display: none;
  }
`;

const GearIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  background: transparent;
  border: 0;
  cursor: pointer;
  color: #fff;

  transition: transform 0.2s ease;

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    transform: rotate(30deg);
    color: #fff;
  }
`;

const SignOutBtn = styled.button`
  background: #000;
  color: #fff;
  border: 0;
  padding: 8px 18px;
  border-radius: 6px;

  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;

  @media (max-width: 1499px) {
    display: none;
  }

  &:hover {
    background: #1a1a1a;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }
`;

/* ===== Status UI ===== */

const StatusIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;

  margin-left: 6px;
`;

const StatusRing = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 999px;
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;


const StatusRingFill = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 999px;

  background: conic-gradient(
    rgba(0, 0, 0, 0.95) calc(var(--p) * 1%),
    rgba(255, 255, 255, 0.35) 0
  );

  -webkit-mask: radial-gradient(circle, transparent 58%, #000 60%);
  mask: radial-gradient(circle, transparent 58%, #000 60%);

  position: relative;

  &::after {
    content: '';
    position: absolute;
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.85);
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.55);
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  animation: ${(p) => (p.$indeterminate ? 'spin 1.1s linear infinite' : 'none')};

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const StatusPopover = styled.div`
  position: fixed;
  transform: translateX(-100%);

  width: 320px;
  max-width: min(92vw, 360px);

  background: #fff;
  border-radius: 12px;
  box-shadow: 0 14px 38px rgba(0, 0, 0, 0.18);
  border: 1px solid rgba(0, 0, 0, 0.08);

  overflow: hidden;
  z-index: 3000;
`;

const StatusHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: 12px 14px;
  background: rgba(233, 112, 36, 0.08);

  .title {
    font-weight: 800;
    font-size: 14px;
    color: #111;
  }

  .pct {
    font-weight: 700;
    font-size: 13px;
    color: #111;
  }
`;

const StatusDivider = styled.div`
  height: 1px;
  background: rgba(0, 0, 0, 0.08);
`;

const StatusList = styled.div`
  padding: 10px 12px;
`;

const StatusRow = styled.div`
  display: grid;
  grid-template-columns: 14px 1fr 18px;
  gap: 10px;
  align-items: start;

  padding: 10px 8px;
  border-radius: 10px;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }

  .text .label {
    font-weight: 650;
    color: #111;
    font-size: 13px;
    line-height: 1.2;
  }

  .text .detail {
    margin-top: 3px;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.6);
    line-height: 1.25;
  }

  .state {
    text-align: right;
    font-weight: 900;
    color: rgba(0, 0, 0, 0.8);
  }
`;

const StatusDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 999px;
  margin-top: 3px;

  background: rgba(0, 0, 0, 0.18);

  &[data-state='done'] {
    background: rgba(0, 0, 0, 0.85);
  }

  &[data-state='active'] {
    background: rgba(233, 112, 36, 0.95);
  }

  &[data-state='error'] {
    background: rgba(180, 0, 0, 0.9);
  }
`;

const StatusFooter = styled.div`
  padding: 10px 14px;

  .hint {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.6);
  }
`;

const HamburgerBtn = styled.button`
  display: none;
  align-items: center;
  justify-content: center;

  background: transparent;
  border: 0;
  cursor: pointer;
  color: #fff;
  padding: 4px;

  svg {
    width: 22px;
    height: 22px;
  }

  &:hover {
    color: rgba(255, 255, 255, 0.75);
  }

  @media (max-width: 1499px) {
    display: flex;
  }
`;

const MobileMenu = styled.div`
  display: none;
  flex-direction: column;
  overflow: hidden;
  max-height: 0;
  transition: max-height 0.3s ease;

  border-top: 1px solid rgba(255, 255, 255, 0.2);
  margin-top: 8px;

  @media (max-width: 1499px) {
    display: flex;
    max-height: ${({ $open }) => ($open ? 'calc(100dvh - 72px)' : '0')};
    overflow-y: ${({ $open }) => ($open ? 'auto' : 'hidden')};
    -webkit-overflow-scrolling: touch;
  }
`;

const MobileMenuDivider = styled.div`
  display: none;
  height: 1px;
  background: rgba(255, 255, 255, 0.25);
  margin: 8px 0;

  @media (max-width: 520px) {
    display: block;
  }
`;

const MobileMenuFooter = styled.div`
  display: none;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 8px;

  @media (max-width: 520px) {
    display: flex;
  }
`;

const MobileUserLabel = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  font-weight: 600;
  padding: 4px 8px 0;
`;

const MobileFooterActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 0 4px;
`;

const MobileFooterBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  border-radius: 8px;
  padding: 10px 14px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s ease;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.35);
  }

  &.sign-out {
    background: #000;
    border-color: #000;
  }

  &.sign-out:hover {
    background: #1a1a1a;
  }
`;

const MobileNavLink = styled.button`
  border: 0;
  background: transparent;
  cursor: pointer;

  color: #fff;
  font-size: 15px;
  font-weight: 500;
  text-align: left;

  padding: 12px 8px;
  position: relative;
  transition: color 0.2s ease, background 0.15s ease;
  white-space: normal;
  word-break: break-word;

  &:hover {
    color: #000;
  }

  &.active {
    font-weight: 600;
  }

  &.active::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 16px;
    background: #fff;
    border-radius: 2px;
  }
`;
